import { MAX_MAGNETS, PHYSICS } from '../config.ts';
import { set, type Vec2 } from '../math/vec2.ts';
import type { Magnet } from './Magnet.ts';

/**
 * The magnetic field, as the game defines it.
 *
 * Deliberately not Maxwell. A magnet is a point source whose influence falls off as
 * 1/d^falloff and whose sign is its polarity; the field at a point is the sum over
 * magnets. The renderer visualises the *same* equation (see FIELD_GLSL below) so the
 * picture on screen can never disagree with what the fluid actually does.
 *
 * Two quantities are exposed:
 *   - force:     a vector, what the simulation applies to fluid particles.
 *   - potential: a scalar, what the visualisation draws contours and tint from.
 */
export class MagneticField {
  readonly magnets: Magnet[] = [];

  /**
   * Packed for the GPU: (x, y, signedStrength, spikeIgnition) per magnet.
   *
   * The fourth slot carries presentation state rather than anything the field maths needs
   * — no shader has ever wanted a magnet's body radius, and the spike pass does want to
   * know whether this magnet's crown has already formed.
   */
  readonly packed = new Float32Array(MAX_MAGNETS * 4);

  get count(): number {
    return Math.min(this.magnets.length, MAX_MAGNETS);
  }

  /** Refresh the GPU-side copy. Called once per frame, not per particle. */
  pack(): Float32Array {
    const packed = this.packed;
    packed.fill(0);
    const count = this.count;
    for (let i = 0; i < count; i++) {
      const magnet = this.magnets[i];
      const o = i * 4;
      packed[o] = magnet.position.x;
      packed[o + 1] = magnet.position.y;
      packed[o + 2] = magnet.signedStrength;
      packed[o + 3] = magnet.spikeIgnition;
    }
    return packed;
  }

  /**
   * Magnetic acceleration at (x, y) for a unit-susceptibility particle.
   * Positive polarity attracts, negative repels.
   */
  sampleForce(x: number, y: number, out: Vec2): Vec2 {
    const { magneticStrength, magneticFalloff, magneticMinDistance, magneticMaxForce } = PHYSICS;
    const minDistance = magneticMinDistance;
    let fx = 0;
    let fy = 0;

    const count = this.count;
    for (let i = 0; i < count; i++) {
      const magnet = this.magnets[i];
      const dx = magnet.position.x - x;
      const dy = magnet.position.y - y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      if (distance < 1e-4) continue;

      // Clamping the distance (rather than the result) keeps the force finite at the
      // centre without changing its direction.
      const clamped = distance < minDistance ? minDistance : distance;
      const magnitude =
        (magnet.signedStrength * magneticStrength) / Math.pow(clamped, magneticFalloff);

      fx += (dx / distance) * magnitude;
      fy += (dy / distance) * magnitude;
    }

    // Plain sqrt rather than Math.hypot: this sits inside the per-particle, per-tick
    // force clamp, and hypot carries an overflow-safe slow path that costs real time
    // here for a guarantee these magnitudes never need.
    const magnitude = Math.sqrt(fx * fx + fy * fy);
    if (magnitude > magneticMaxForce) {
      const k = magneticMaxForce / magnitude;
      fx *= k;
      fy *= k;
    }
    return set(out, fx, fy);
  }

  /** Scalar potential used by the visualisation (and handy for debugging). */
  samplePotential(x: number, y: number): number {
    const minDistance = PHYSICS.magneticMinDistance;
    let potential = 0;
    const count = this.count;
    for (let i = 0; i < count; i++) {
      const magnet = this.magnets[i];
      const dx = magnet.position.x - x;
      const dy = magnet.position.y - y;
      const distance = Math.max(Math.sqrt(dx * dx + dy * dy), minDistance);
      potential += (magnet.signedStrength * POTENTIAL_SCALE) / distance;
    }
    return potential;
  }
}

/** Keeps the potential in a pleasant 0..5-ish range. */
export const POTENTIAL_SCALE = 300;

/**
 * Reference acceleration that defines a magnet's visible "reach".
 *
 * The background glow has to tell the player how far a magnet actually pulls, so it is
 * derived from the force law rather than picked to look nice: reach is the distance at
 * which the magnet's acceleration falls to this value.
 */
export const FIELD_REACH_ACCEL = 300;

/** Distance at which a magnet's pull drops to FIELD_REACH_ACCEL, in world units. */
export function magnetReach(signedStrength: number): number {
  const numerator = Math.abs(signedStrength) * PHYSICS.magneticStrength;
  return Math.pow(numerator / FIELD_REACH_ACCEL, 1 / PHYSICS.magneticFalloff);
}

/**
 * GLSL mirror of the functions above.
 *
 * Shared by every shader that needs the field. Shaders must never invent their own
 * field maths — if this and MagneticField drift apart, the game starts lying to the
 * player about what a magnet will do.
 *
 * Expects these uniforms to be declared by the including shader:
 *   uniform vec4  uMagnets[MAX_MAGNETS];  // xy = position, z = signed strength, w = radius
 *   uniform float uMagnetCount;
 *   uniform vec3  uFieldParams;           // x = strength, y = falloff, z = min distance
 */
export const FIELD_GLSL = /* glsl */ `
#define MAX_MAGNETS ${MAX_MAGNETS}
#define POTENTIAL_SCALE ${POTENTIAL_SCALE.toFixed(1)}

vec2 magneticForce(vec2 p) {
    vec2 force = vec2(0.0);
    int count = int(uMagnetCount);
    for (int i = 0; i < MAX_MAGNETS; i++) {
        if (i >= count) break;
        vec4 magnet = uMagnets[i];
        vec2 delta = magnet.xy - p;
        float distance = length(delta);
        if (distance < 1e-4) continue;
        float clamped = max(distance, uFieldParams.z);
        float magnitude = magnet.z * uFieldParams.x / pow(clamped, uFieldParams.y);
        force += (delta / distance) * magnitude;
    }
    return force;
}

/** Distance at which a magnet's pull drops to the reference acceleration. */
float magnetReach(float signedStrength) {
    return pow(abs(signedStrength) * uFieldParams.x / ${FIELD_REACH_ACCEL}.0, 1.0 / uFieldParams.y);
}

float magneticPotential(vec2 p) {
    float potential = 0.0;
    int count = int(uMagnetCount);
    for (int i = 0; i < MAX_MAGNETS; i++) {
        if (i >= count) break;
        vec4 magnet = uMagnets[i];
        float distance = max(length(magnet.xy - p), uFieldParams.z);
        potential += magnet.z * POTENTIAL_SCALE / distance;
    }
    return potential;
}
`;
